// Core security utilities shared across the application
// This file contains the fundamental security functions for JWT-based authentication

import { Permission, User, UserProfile, UserRole } from '../types/auth';

// Security utility functions
export class SecurityUtils {
  /**
   * Validate if the current connection is secure
   */
  static isSecureConnection(targetWindow: Window = window): boolean {
    // Check if running on HTTPS or localhost (for development)
    return (
      targetWindow.location.protocol === 'https:' ||
      targetWindow.location.hostname === 'localhost' ||
      targetWindow.location.hostname === '127.0.0.1'
    );
  }

  /**
   * Check if user has permission (or is admin)
   */
  static hasPermission(user: User | null, permission: Permission): boolean {
    if (!user || !user.isActive) return false;
    if (user.isAdmin) return true;
    return user.permissions.includes(permission);
  }

  /**
   * Check if user is admin
   */
  static isAdmin(user: User | null): boolean {
    return user?.isAdmin ?? false;
  }

  /**
   * Validate user role (ADMIN = isAdmin, others = permission)
   */
  static hasRole(user: User | null, requiredRole: UserRole): boolean {
    if (!user || !user.isActive) return false;
    if (requiredRole === UserRole.ADMIN) return user.isAdmin;
    return user.permissions.includes(requiredRole as unknown as Permission);
  }

  /**
   * Check if user has any of the required roles
   */
  static hasAnyRole(user: User | null, requiredRoles: UserRole[]): boolean {
    if (!user || !user.isActive) return false;
    if (requiredRoles.includes(UserRole.ADMIN) && user.isAdmin) return true;
    return requiredRoles.some((r) =>
      r === UserRole.ADMIN ? user.isAdmin : user.permissions.includes(r as unknown as Permission)
    );
  }

  /**
   * Check if user has all required roles
   */
  static hasAllRoles(user: User | null, requiredRoles: UserRole[]): boolean {
    if (!user || !user.isActive) return false;
    return requiredRoles.every((r) =>
      r === UserRole.ADMIN ? user.isAdmin : user.permissions.includes(r as unknown as Permission)
    );
  }

  /**
   * Sanitize user data for memory storage (remove sensitive information)
   */
  static sanitizeUserForMemory(
    user: Record<string, unknown> | UserProfile
  ): User {
    const u = user as UserProfile;
    return {
      id: u.id,
      username: u.username,
      permissions: u.permissions,
      isAdmin: u.isAdmin,
      isActive: u.isActive,
      inactivityLimitMs: u.inactivityLimitMs ?? null,
      sessionKey: u.sessionKey ?? null,
    };
  }

  /**
   * Secure logout (clear any cached data from memory)
   * JWT tokens are cleared by AuthService/tokenStore; this clears browser caches
   */
  static secureLogout(): void {
    // Clear any cached user data
    if (window.caches) {
      caches
        .keys()
        .then((names) => {
          names.forEach((name) => {
            caches.delete(name).catch(() => {
              // Silently ignore cache deletion errors
            });
          });
        })
        .catch(() => {
          // Silently ignore cache key retrieval errors
        });
    }
  }
}

// Export individual functions for direct import
export const hasPermission = SecurityUtils.hasPermission;
export const isAdmin = SecurityUtils.isAdmin;
export const hasRole = SecurityUtils.hasRole;
export const hasAnyRole = SecurityUtils.hasAnyRole;
export const hasAllRoles = SecurityUtils.hasAllRoles;
export const sanitizeUserForMemory = SecurityUtils.sanitizeUserForMemory;
export const isSecureConnection = (targetWindow: Window = window): boolean =>
  SecurityUtils.isSecureConnection(targetWindow);
export const secureLogout = SecurityUtils.secureLogout;
